import type { EventsFormRef } from '@/pages/Events/EventsForm';
import {
	type PayloadAction,
	type Slice,
	type SliceCaseReducers,
	createSelector,
	createSlice,
} from '@reduxjs/toolkit';
import {
	EventCategoryEnum,
	type EventFormData,
	type EventResponse,
} from 'graphql/gql-types';
import type { RootState } from 'redux/store';
export interface eventsFormState {
	formValues: EventFormData;
	eventsFormRef: EventsFormRef | null;
	isEventsFormDirty: boolean;
	isEventNameReadonly: boolean;
	selectedEvents: EventResponse[];
	currentEvent: Partial<EventFormData>;
}

export const initialEventFormState: eventsFormState = {
	formValues: {
		event_id: '',
		name: '',
		created_at: '',
		created_by_id: '',
		updated_at: '',
		updated_by_id: '',
		recurs: false,
		occ_impact: 0,
		category: EventCategoryEnum.general,
		property_id: '',
		records: [{ start_date: '', end_date: '', id: '', event_id: '' }],
	},
	eventsFormRef: null,
	isEventsFormDirty: false,
	isEventNameReadonly: false,
	selectedEvents: [],
	currentEvent: {},
};

const reducers: SliceCaseReducers<eventsFormState> = {
	updateEventsFormValues: (
		state,
		action: PayloadAction<Partial<EventsFormRef>>,
	) => {
		state.formValues = { ...state.formValues, ...action.payload };
		if (state.eventsFormRef) {
			if (state.eventsFormRef.resetForm) {
				state.eventsFormRef.resetForm({ values: state.formValues });
			}
			if (typeof state.eventsFormRef.dirty !== 'undefined') {
				state.isEventsFormDirty = state.eventsFormRef.dirty;
			}
		}
	},

	setEventsFormRef: (state, action: PayloadAction<EventsFormRef>) => {
		state.eventsFormRef = action.payload;
	},
	setIsEventsFormDirty: (state, action: PayloadAction<boolean>) => {
		state.isEventsFormDirty = action.payload;
	},
	setIsEventNameReadonly: (state, action: PayloadAction<boolean>) => {
		state.isEventNameReadonly = action.payload;
	},
	setSelectedEvents(state, action: PayloadAction<EventResponse[]>) {
		state.selectedEvents = action.payload;
	},
	setCurrentEvent(state, action: PayloadAction<EventFormData>) {
		state.currentEvent = action.payload;
	},
};

export const eventsFormSlice: Slice<eventsFormState, typeof reducers> =
	createSlice({
		name: 'eventsForm',
		initialState: initialEventFormState,
		reducers,
	});

export const selectEventsFormData = createSelector(
	(state: RootState) => state.eventsForm,
	(eventsForm) => eventsForm.formValues,
);

export const selectSelectedEvents = createSelector(
	(state: RootState) => state.eventsForm,
	(eventsForm) => eventsForm.selectedEvents,
);

export const selectCurrentEvent = createSelector(
	(state: RootState) => state.eventsForm,
	(eventsForm) => eventsForm.currentEvent,
);

export const {
	updateEventsFormValues,
	setEventsFormRef,
	setIsEventsFormDirty,
	setIsEventNameReadonly,
	setSelectedEvents,
	setCurrentEvent,
} = eventsFormSlice.actions;
export default eventsFormSlice.reducer;
